import React from 'react';

const hasValue = (value) => value !== null && value !== undefined && value !== '';

function numberValue(value) {
  if (!hasValue(value)) return '';
  const n = Number(value);
  const raw = Number.isFinite(n) ? String(Math.trunc(n)) : String(value).split('.')[0];
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function parseNumber(value) {
  const integerPart = String(value || '').replace(/,/g, '').split('.')[0];
  const digits = integerPart.replace(/[^\d]/g, '');
  return digits === '' ? null : Number(digits);
}

const FLIGHT_FIELDS = [
  { key: 'passenger_type', fieldKey: 'flight.passenger_type', label: '구분', required: true, type: 'select', compact: true },
  { key: 'base_fare', fieldKey: 'flight.base_fare', label: '항공운임', required: true, type: 'amount' },
  { key: 'fuel_surcharge', fieldKey: 'flight.fuel_surcharge', label: '유류할증료', required: true, type: 'amount' },
  { key: 'taxes', fieldKey: 'flight.taxes', label: '공항세', required: true, type: 'amount' },
  { key: 'total_payment', fieldKey: 'flight.total_payment', label: '총액 자동', type: 'readonly' },
  { key: 'airline', fieldKey: 'flight.airline', label: '항공사' },
  { key: 'flight_number', fieldKey: 'flight.flight_number', label: '편명', compact: true },
  { key: 'departure_airport', fieldKey: 'flight.departure_airport', label: '출발공항' },
  { key: 'arrival_airport', fieldKey: 'flight.arrival_airport', label: '도착공항' },
  { key: 'departure_at', fieldKey: 'flight.departure_at', label: '출발' },
  { key: 'arrival_at', fieldKey: 'flight.arrival_at', label: '도착' },
  { key: 'passenger_name', fieldKey: 'flight.passenger_name', label: '탑승자' },
  { key: 'seat_no', fieldKey: 'flight.seat_no', label: '좌석', compact: true },
];

export default function ReservationFlightRows({
  rows = [],
  showField,
  paxOptions = [],
  flightTotal,
  onChange,
  onRemove,
}) {
  const visibleFields = FLIGHT_FIELDS.filter((field) => showField(field.fieldKey));
  if (!rows.length) return <div style={emptyStyle}>항공편 없음 - "+ 추가" 클릭</div>;

  return (
    <div style={listStyle}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} style={rowStyle}>
          <div style={rowHeaderStyle}>
            <strong style={rowTitleStyle}>항공 #{rowIndex + 1}</strong>
            <button type="button" onClick={() => onRemove(rowIndex)} style={deleteBtnStyle}>삭제</button>
          </div>
          <div style={fieldGridStyle}>
            {visibleFields.map((field) => (
              <CompactField
                key={field.key}
                field={field}
                value={field.key === 'total_payment' ? flightTotal(row) : row[field.key]}
                paxOptions={paxOptions}
                onChange={(value) => onChange(rowIndex, field.key, value)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CompactField({ field, value, paxOptions, onChange }) {
  return (
    <label style={fieldWrapStyle(field.compact)}>
      <span style={labelStyle}>
        {field.label}
        {field.required && <span style={requiredMark}> *</span>}
      </span>
      <FieldControl field={field} value={value} paxOptions={paxOptions} onChange={onChange} />
    </label>
  );
}

function FieldControl({ field, value, paxOptions, onChange }) {
  if (field.type === 'readonly') {
    return <div style={readonlyStyle}>{Number(value || 0).toLocaleString('ko-KR')}원</div>;
  }

  if (field.type === 'select') {
    return (
      <select value={value ?? ''} onChange={(event) => onChange(event.target.value || null)} style={inputStyle}>
        <option value="">선택</option>
        {paxOptions.map((option) => (
          <option key={option.value ?? option} value={option.value ?? option}>{option.label ?? option}</option>
        ))}
      </select>
    );
  }

  if (field.type === 'amount') {
    return (
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9,]*"
        value={numberValue(value)}
        onFocus={(event) => event.target.select()}
        onChange={(event) => onChange(parseNumber(event.target.value))}
        style={amountInputStyle}
      />
    );
  }

  return (
    <input value={value ?? ''} onChange={(event) => onChange(event.target.value || null)} style={inputStyle} />
  );
}

const listStyle = { display: 'grid', gap: 6, minWidth: 0 };
const rowStyle = {
  display: 'grid',
  gridTemplateColumns: '86px minmax(0, 1fr)',
  gap: 7,
  alignItems: 'end',
  padding: 7,
  border: '1px solid var(--border)',
  borderRadius: 7,
  background: 'var(--bg)',
  minWidth: 0,
};
const rowHeaderStyle = { display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'space-between', minWidth: 0 };
const rowTitleStyle = { fontSize: 12, color: 'var(--text)', whiteSpace: 'nowrap' };
const fieldGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))', gap: 6, minWidth: 0 };
const fieldWrapStyle = (compact) => ({ display: 'grid', gap: 2, minWidth: compact ? 82 : 0 });
const labelStyle = { fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.1, whiteSpace: 'nowrap' };
const requiredMark = { color: '#dc2626' };
const inputStyle = {
  width: '100%',
  height: 30,
  padding: '5px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--bg-card)',
  color: 'var(--text)',
  boxSizing: 'border-box',
  fontSize: 12,
};
const amountInputStyle = { ...inputStyle, textAlign: 'right' };
const readonlyStyle = {
  height: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '0 2px',
  color: 'var(--text)',
  fontSize: 12,
  fontWeight: 800,
  whiteSpace: 'nowrap',
};
const deleteBtnStyle = {
  height: 26,
  padding: '2px 6px',
  background: 'transparent',
  color: '#dc2626',
  border: '1px solid #dc2626',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 10,
};
const emptyStyle = { padding: 8, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 };
